import queryString from 'query-string';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';

import userActions from '@/actions/userActions';
import userApi from '@/util/userApi';
import { uncacheImage } from '@account-settings/utils';
import { QueryConfig } from '@shared/api';
import { usePathnameContext } from '@shared/hooks/usePathContext';
import { ServerUser, User } from '@shared/types';
import { getWorkspaceName } from '@shared/utils/url';
import { dialogTypes } from '@/constants/alertTypes';
import {
  CURRENT_USER_RETRY_COUNT,
  ERROR_MESSAGE_FALLBACK
} from '@/constants/authConstants';
import alertsUtil from '@/util/alertsUtil';

const mapUser = (serverUser: ServerUser): User => ({
  ...serverUser,
  username: serverUser?.userName,
  email: serverUser?.email,
  profileImage: serverUser?.profileImages?.large
    ? uncacheImage(serverUser?.profileImages?.large)
    : '',
  isLoggedInWithGithub: serverUser?.gitHub,
  isLoggedInWithGoogle: serverUser?.google,
  apiKey: (serverUser?.apiKeys?.length && serverUser.apiKeys[0]) || '',
  organizations: serverUser?.organizations,
  shownGetStarted: serverUser?.shownGetStartedFlag,
  hideHelpCenter: serverUser?.hideHelpCenterFlag,
  hasExperiments: serverUser?.hasExperiments,
  shownPostSignupForm: serverUser?.showPostSignupForm,
  loading: false
});

const getCurrentUser = async (workspaceName: string): Promise<User> => {
  const user = await userApi.fetchUser({ workspaceName, apiKey: undefined });

  return mapUser(user.data);
};

function useWorkspaceName() {
  // in order to re-trigger on change the pathname
  usePathnameContext();

  return getWorkspaceName();
}

export default function useCurrentUser(config: QueryConfig<User> = {}) {
  const dispatch = useDispatch();
  const workspaceName = useWorkspaceName();
  const { workspaceName: workspaceNameInQuery = '' } = queryString.parse(
    window.location.search
  );

  return useQuery(
    ['current-user', { workspaceName }],
    () =>
      getCurrentUser(workspaceName || (workspaceNameInQuery as string) || ''),
    {
      keepPreviousData: true,
      retry: CURRENT_USER_RETRY_COUNT,
      ...config,
      onSuccess: data => {
        dispatch(userActions.receiveUser(mapUser(data)));
      },
      onError: error => {
        const response = (error as AxiosError<{ msg: string; code: number }>)
          ?.response;
        dispatch(
          alertsUtil.openErrorDialog(
            dialogTypes.CATCH_ERROR_API,
            response?.data?.msg || ERROR_MESSAGE_FALLBACK
          )
        );
      }
    }
  );
}
